import { useState, useMemo, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Form } from 'react-final-form';
import { Row, Spacer, Column, RightDrawer, FormInput } from '.';
import { useObservable } from '@aesop-fables/scrinium';
import { useService } from '@aesop-fables/containr-react';
import { DrillDownWizard, drillDownWizardKey } from '../services/drillDown';
import { AvailablePricebooks, PricebookBundle } from '../models/PricebookData';
import { PaidByEnum } from '../models/LicenseData';
import {
  RestrictionMode,
  sortPricebooksByPayerAndName,
  getFooterMessage,
} from '../dashboard/pricebook-restrictions/helpers/pricebookRestrictionUtils';
import PricebookRestrictionSelector from '../dashboard/pricebook-restrictions/components/PricebookRestrictionSelector';
import { useAddOnPricebookLookup } from '../hooks/useAddOnPricebookLookup';

const EMPTY_SET = new Set<number>();

interface Props {
  drawerOpen: boolean;
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSubmit: (...values: any[]) => void;
  groupAvailablePricebooks?: AvailablePricebooks;
  allPricebookBundles?: PricebookBundle[];
}

const CreateTeamDrawer: React.FC<Props> = ({
  drawerOpen,
  setDrawerOpen,
  onSubmit,
  groupAvailablePricebooks,
  allPricebookBundles,
}) => {
  const drillDownWizard = useService<DrillDownWizard>(drillDownWizardKey);
  const tier = useObservable(drillDownWizard.tier$);

  const showPricebookRestrictions = Boolean(groupAvailablePricebooks);
  const [restrictionMode, setRestrictionMode] = useState<RestrictionMode>('inherit');
  const [selectedPricebookIds, setSelectedPricebookIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!drawerOpen) return;
    setRestrictionMode('inherit');
    setSelectedPricebookIds(new Set());
  }, [drawerOpen]);

  const { memberPaysPricebooks, orgPaysPricebooks, pricebooksWithAddOns } = useMemo(() => {
    const bundles =
      restrictionMode === 'advanced'
        ? (allPricebookBundles ?? [])
        : (groupAvailablePricebooks?.pricebookBundleDtos ?? []);
    // Premium add-on pricebooks are hidden; selecting the base pricebook pairs its add-on on the backend
    const sorted = sortPricebooksByPayerAndName(bundles.filter(pb => !pb.pricebook.addOn));
    return {
      memberPaysPricebooks: sorted.filter(pb => pb.pricebook.paidBy === PaidByEnum.User),
      orgPaysPricebooks: sorted.filter(pb => pb.pricebook.paidBy === PaidByEnum.Org),
      pricebooksWithAddOns: bundles,
    };
  }, [restrictionMode, groupAvailablePricebooks, allPricebookBundles]);

  const addOnPricebookLookup = useAddOnPricebookLookup(pricebooksWithAddOns);

  const visiblePricebookIds = useMemo(() => {
    const ids = new Set<number>();
    for (const pb of memberPaysPricebooks) ids.add(pb.pricebook.id);
    for (const pb of orgPaysPricebooks) ids.add(pb.pricebook.id);
    return ids;
  }, [memberPaysPricebooks, orgPaysPricebooks]);

  const selectedVisibleIds = useMemo(
    () => new Set([...selectedPricebookIds].filter(id => visiblePricebookIds.has(id))),
    [selectedPricebookIds, visiblePricebookIds],
  );

  const handleTogglePricebook = (pricebookId: number) => {
    if (restrictionMode === 'inherit') return;
    setSelectedPricebookIds(prev => {
      const next = new Set(prev);
      if (next.has(pricebookId)) {
        next.delete(pricebookId);
      } else {
        next.add(pricebookId);
      }
      return next;
    });
  };

  const { text: footerText, boldSegment } = getFooterMessage(
    restrictionMode,
    selectedVisibleIds.size,
    'group',
    'team',
  );
  const footerMessage = (() => {
    if (!boldSegment) return footerText;
    const [prefix, suffix] = footerText.split(boldSegment);
    return (
      <>
        {prefix}
        <strong>{boldSegment}</strong>
        {suffix}
      </>
    );
  })();

  const submitHandler = async (values: any) => {
    try {
      onSubmit({
        ...values,
        restrictionMode,
        selectedPricebookIds: [...selectedVisibleIds],
      });
      setDrawerOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  const validateName = (value: string) => {
    if (value.length > 128) {
      return 'Name should not exceed 128 characters.';
    }
    return undefined;
  };

  return (
    <RightDrawer
      open={drawerOpen}
      setOpen={setDrawerOpen}
      title={`Create ${tier?.toLowerCase()}`}
      footerShadow={showPricebookRestrictions}
      footer={
        showPricebookRestrictions ? (
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Button
              variant='outlined'
              color='secondary'
              onClick={() => setDrawerOpen(false)}
              sx={{ minWidth: '100px', typography: 'p14SemiBold' }}>
              Cancel
            </Button>
            <Typography
              variant='p16'
              color='secondary.main'
              sx={{ textAlign: 'center', flex: 1, mx: 2 }}>
              {footerMessage}
            </Typography>
            <Button
              id='create-team-submit'
              variant='tertiary'
              color='primary'
              type='submit'
              form='create-team-form'
              sx={{ minWidth: '100px', typography: 'p14SemiBold' }}>
              Create {tier?.toLowerCase()}
            </Button>
          </Box>
        ) : undefined
      }>
      <Form onSubmit={submitHandler}>
        {({ handleSubmit }) => (
          <form id='create-team-form' onSubmit={handleSubmit}>
            <Spacer height='xxs' />
            <Column>
              <Box display='flex' alignItems='center' gap='20px'>
                <Typography
                  variant='body1'
                  color='info.main'
                  sx={{ whiteSpace: 'nowrap', position: 'relative', top: '3px' }}>
                  Team name
                </Typography>
                <Box sx={{ '& .MuiInput-root': { mt: 0 }, '& .MuiInput-input': { py: 0 } }}>
                  <FormInput fieldName='name' validate={validateName} required autoFocus />
                </Box>
              </Box>
              <Spacer height='sm' />
            </Column>

            {showPricebookRestrictions ? (
              <PricebookRestrictionSelector
                heading='Would you like to inherit group pricebook restrictions or override?'
                parentTierLabel='group'
                tierLabel='team'
                mode={restrictionMode}
                onModeChange={setRestrictionMode}
                memberPaysPricebooks={memberPaysPricebooks}
                orgPaysPricebooks={orgPaysPricebooks}
                addOnPricebookLookup={addOnPricebookLookup}
                inUsePricebookIds={EMPTY_SET}
                selectedPricebookIds={selectedPricebookIds}
                onTogglePricebook={handleTogglePricebook}
              />
            ) : (
              <Row style={{ justifyContent: 'space-between' }}>
                <Button color='secondary' variant='outlined' onClick={() => setDrawerOpen(false)}>
                  Cancel
                </Button>
                <Button color='primary' variant='tertiary' onClick={handleSubmit}>
                  Create {tier?.toLowerCase()}
                </Button>
              </Row>
            )}
          </form>
        )}
      </Form>
    </RightDrawer>
  );
};

export default CreateTeamDrawer;
